import { OTHER_COLOR, default as GameState, Color } from "../board/GameState";

type FormatString = string | Array<any>;
const F = (
  strings: TemplateStringsArray,
  ...values: Array<any>
): FormatString =>
  strings.reduce(
    (acc: string[], s: string, i) =>
      i === strings.length - 1 ? acc.concat([s]) : acc.concat([s, values[i]]),
    []
  );

const pluralize = (num: number, thing: string) =>
  `${num} ${thing}${num !== 1 ? "s" : ""}`;
const capitalize = (val: any) => ({ capitalize: val });
const conjugate = (verb: string, subject: any) => ({
  conjugate: verb,
  for: subject,
});
// Replaces a color name with a viewer-appropriate string
const colorName = (color: Color) => ({ color });

export default class GameDescription {
  game: GameState;
  nextAction: "move" | null | "offer_duel" | "resolve_duel";
  viewer: Color | undefined;

  constructor(
    game: GameState,
    nextAction: "move" | null | "offer_duel" | "resolve_duel",
    viewer: Color | undefined
  ) {
    this.game = game;
    this.nextAction = nextAction;
    this.viewer = viewer;
  }

  toString(): string {
    return this.tokenToString(capitalize(this.describe()));
  }

  tokenToString = (t: unknown): string => {
    if (Array.isArray(t)) {
      return t.map(this.tokenToString).join("");
    } else if (t === null || t === undefined) {
      return "";
    } else if (typeof t !== "object") {
      return t as any;
    } else {
      const command: any = t;
      if ("color" in command) {
        return this.viewer && this.viewer === command["color"]
          ? "you"
          : command["color"];
      } else if ("conjugate" in command) {
        const verb = command.conjugate;
        const subject = this.tokenToString(command.for);
        const person = subject === "you" ? 2 : 3;
        return person === 2 ? verb : verb + "s";
      } else if ("capitalize" in command) {
        const val = this.tokenToString(command.capitalize);
        return val.slice(0, 1).toUpperCase() + val.slice(1);
      } else {
        throw new Error("Unknown command " + JSON.stringify(command));
      }
    }
  };

  describe(): FormatString {
    const { game, nextAction } = this;
    if (game.winner) {
      return game.winner === "draw"
        ? "Game over. Draw."
        : this.viewer === game.winner
        ? "You win the game!"
        : this.viewer === undefined
        ? F`Game over. ${capitalize(
            colorName(game.winner as any)
          )} wins the game.`
        : "Game over. You lose.";
    } else if (!game.lastMove) {
      return this.playerToMove(
        "Make the first move.",
        "Waiting for the first move."
      );
    }
    const [lastMove, ...duels] = game.lastMove.split(":");
    if (nextAction === "resolve_duel") {
      return this.playerWhoMoved(
        F`${colorName(
          OTHER_COLOR[game.lastMoveColor()]
        )} challenged your move ${lastMove}. Respond.`,
        F`Waiting for ${colorName(
          game.lastMoveColor()
        )} to respond to the challenge.`
      );
    } else {
      const duelResult =
        duels.length > 0 ? this.describeDuel(duels[duels.length - 1]) : null;
      const activeColor = game.canChallenge()
        ? OTHER_COLOR[game.lastMoveColor()]
        : game.toMove;
      const actions = [];
      if (activeColor === game.toMove) {
        if (game.kingTurn) {
          actions.push("move (king turn)");
        } else {
          actions.push("move");
        }
      }
      if (game.canChallenge()) {
        actions.push("challenge");
      }
      const actionStr = actions.join(" or ");
      // XXX - include captured pieces
      return this.playerToMove(
        F`${colorName(game.lastMoveColor())} moved ${lastMove}. ${duelResult}${
          game.canChallenge() ? "You may challenge or move." : "Your move."
        }`,
        F`${colorName(
          game.lastMoveColor()
        )} moved ${lastMove}. ${duelResult}Waiting for ${colorName(
          activeColor
        )} to ${actionStr}.`
      );
    }
  }

  describeDuel(duel: string): any {
    if (duel.length === 0) return "";
    const bid = Number(duel[0]);
    const response = Number(duel[1]);
    const gained = duel[2] === "+";
    const bidder = colorName(OTHER_COLOR[this.game.lastMoveColor()]);
    const responder = colorName(this.game.lastMoveColor());
    // XXX - look at captured peices, use "paid to challenge" if true
    const challenge_verb = "challenged";
    const winner = response >= bid ? responder : bidder;
    const result =
      response === bid && bid === 0
        ? gained
          ? F`${responder} ${conjugate("win", responder)} and ${conjugate(
              "gain",
              responder
            )} a stone.`
          : F`${bidder} ${conjugate("lose", bidder)} and ${conjugate(
              "lose",
              bidder
            )} a stone.`
        : F`${winner} ${conjugate("win", winner)}.`;
    return capitalize(
      F`${bidder} ${challenge_verb} for ${pluralize(
        bid,
        "stone"
      )}; ${responder} bid ${response}. ${capitalize(result)} `
    );
  }

  playerToMove(toMove: FormatString, other: FormatString) {
    return this.viewer === this.game.toMove ? toMove : other;
  }

  playerWhoMoved(whoMoved: FormatString, other: FormatString) {
    return this.viewer === this.game.lastMoveColor() ? whoMoved : other;
  }
}
